import moment from 'moment';
import { hasAuth } from '@/utils/validate';
import { D_BRAND_AUTH, P_DEV, P_TRACK } from '@/menu/auth-const';
import { IConfigV2, ITimeItem, IWidgetConfig, IWidgetItem, IWinSize } from './types';

// 所有小组件列表
export const widgetItems: IWidgetItem[] = [
  { code: 'ClientUsage', name: 'ClientUsage', title: '客户端使用统计', auth: [P_TRACK] },
  { code: 'CrashReport', name: 'ClientCrash', title: '用户出错上报统计', auth: [P_TRACK, P_DEV] },
  { code: 'DownloadRecord', name: 'OssDownload', title: '用户下载统计', auth: [P_TRACK] },
  { code: 'KickedUserRecord', name: 'LoginThresh', title: '用户异常登录统计', auth: [P_TRACK] },
  { code: 'NewProjectStatistics', name: 'DpgIncrement', title: '专案组及样板间新增统计', auth: [P_TRACK] },
];

// 时间段列表
export const dateTypeItems: ITimeItem[] = [
  { text: '本月', value: 'thismonth', parser: parseThisMonth },
  { text: '一周以内', value: '1week', parser: parseOneWeek },
  { text: '一个月内', value: '1month', parser: parseOneMonth },
  { text: '三个月内', value: '3month', parser: parseThreeMonth },
  { text: '一年以内', value: '1year', parser: parseOneYear },
  { text: '今年', value: 'thisyear', parser: parseThisYear },
  { text: '今天', value: 'today', parser: parseToday, hide: true },
  { text: '昨天', value: 'lastday', parser: parseLastday },
  { text: '上周', value: 'lastweek', parser: parseLastweek },
  { text: '上个月', value: 'lastmonth', parser: parseLastmonth },
  { text: '自定义', value: 'interval', parser: parseInterval },
];

// 响应式列宽
const responseCols = [
  { cols: 1, small: 12, normal: 6, large: 3, xlarge: 4, hdpi: 1 },
  { cols: 2, small: 12, normal: 6, large: 3, xlarge: 3, hdpi: 2 },
  { cols: 3, small: 12, normal: 6, large: 3, xlarge: 3, hdpi: 3 },
  { cols: 4, small: 12, normal: 6, large: 4, xlarge: 4, hdpi: 4 },
  { cols: 5, small: 12, normal: 6, large: 5, xlarge: 5, hdpi: 5 },
  { cols: 6, small: 12, normal: 6, large: 6, xlarge: 6, hdpi: 6 },
  { cols: 7, small: 12, normal: 7, large: 7, xlarge: 7, hdpi: 7 },
  { cols: 8, small: 12, normal: 8, large: 8, xlarge: 8, hdpi: 8 },
  { cols: 9, small: 12, normal: 9, large: 9, xlarge: 9, hdpi: 9 },
  { cols: 10, small: 12, normal: 10, large: 10, xlarge: 10, hdpi: 10 },
  { cols: 11, small: 12, normal: 11, large: 11, xlarge: 11, hdpi: 11 },
  { cols: 12, small: 12, normal: 12, large: 12, xlarge: 12, hdpi: 12 },
];

// 权限相关
export function getAuth() {
  return {
    view: hasAuth([P_DEV, P_TRACK]),    // 是否拥有'查看小组件列表'的权限
    brandAuth: hasAuth([D_BRAND_AUTH]), // 是否拥有'企业品牌授权'的权限
  };
}

// 计算响应式列宽
export function parseColNum(colNum: number, windowWidth: number) {
  let size: IWinSize = '';
  if (windowWidth < 900) {
    size = 'small';
  } else if (windowWidth < 1440) {
    size = 'normal';
  } else if (windowWidth < 1920) {
    size = 'large';
  } else if (windowWidth < 2880) {
    size = 'xlarge';
  } else {
    size = 'hdpi';
  }
  return responseCols[colNum - 1][size];
}

// 解析配置
export function parseConfig(config: string) {
  try {
    const result = JSON.parse(config);
    return genConfig(result);
  } catch (e) {
    return genConfig(null);
  }
}

// 生成配置(第二版配置)
export function genConfig(result: IWidgetConfig | null): IConfigV2 {
  const version = result?.version || 1;
  const cols = result?.cols || 6;
  const sortNo = result?.sortNo || -1;
  const period = result?.period || 'thismonth';
  const startTime = period === 'interval' ? (result?.startTime || '') : '';
  const endTime = period === 'interval' ? (result?.endTime || '') : '';
  return { version, cols, sortNo, period, startTime, endTime };
}

// 本月
export function parseThisMonth() {
  const current = moment();
  const startTime = current.startOf('month').format('YYYY-MM-DD');
  const endTime = current.endOf('month').format('YYYY-MM-DD');
  const overTime = moment().endOf('month').format('YYYY-MM-DD');
  const showTime = current.format('YYYY-MM');
  const shortName = '本月';
  return { startTime, endTime, overTime, showTime, shortName };
}

// 今年
export function parseThisYear() {
  const current = moment();
  const startTime = current.startOf('year').format('YYYY-MM-DD');
  const endTime = current.endOf('year').format('YYYY-MM-DD');
  const overTime = moment().endOf('year').format('YYYY-MM-DD');
  const showTime = current.format('YYYY') + '年';
  const shortName = '今年';
  return { startTime, endTime, overTime, showTime, shortName };
}

// 今天
export function parseToday() {
  const today = moment().format('YYYY-MM-DD');
  const startTime = today;
  const endTime = today;
  const overTime = today;
  const showTime = today;
  const shortName = '今天';
  return { startTime, endTime, overTime, showTime, shortName };
}

// 昨天
export function parseLastday() {
  const lastday = moment().subtract(1, 'days').format('YYYY-MM-DD');
  const startTime = lastday;
  const endTime = lastday;
  const overTime = lastday;
  const showTime = lastday;
  const shortName = '昨天';
  return { startTime, endTime, overTime, showTime, shortName };
}

// 上周
export function parseLastweek() {
  const lastWeek = moment().subtract(1, 'weeks');
  const startTime = lastWeek.startOf('isoWeek').format('YYYY-MM-DD');
  const endTime = lastWeek.endOf('isoWeek').format('YYYY-MM-DD');
  const overTime = lastWeek.endOf('isoWeek').format('YYYY-MM-DD');
  const showTime = `${startTime} ~ ${endTime}`;
  const shortName = '上周';
  return { startTime, endTime, overTime, showTime, shortName };
}

// 上个月
export function parseLastmonth() {
  const lastMonth = moment().subtract(1, 'months');
  const startTime = lastMonth.startOf('month').format('YYYY-MM-DD');
  const endTime = lastMonth.endOf('month').format('YYYY-MM-DD');
  const overTime = lastMonth.endOf('month').format('YYYY-MM-DD');
  const showTime = lastMonth.format('YYYY-MM');
  const shortName = '上个月';
  return { startTime, endTime, overTime, showTime, shortName };
}

// 一周以内
export function parseOneWeek() {
  const startTime = moment().subtract(1, 'weeks').add(1, 'days').format('YYYY-MM-DD');
  const endTime = moment().format('YYYY-MM-DD');
  const overTime = moment().format('YYYY-MM-DD');
  const showTime = `${startTime} ~ ${endTime}`;
  const shortName = '一周以内';
  return { startTime, endTime, overTime, showTime, shortName };
}

// 一个月内
export function parseOneMonth() {
  const startTime = moment().subtract(1, 'months').add(1, 'days').format('YYYY-MM-DD');
  const endTime = moment().format('YYYY-MM-DD');
  const overTime = moment().format('YYYY-MM-DD');
  const showTime = `${startTime} ~ ${endTime}`;
  const shortName = '一个月内';
  return { startTime, endTime, overTime, showTime, shortName };
}

// 三个月内
export function parseThreeMonth() {
  const startTime = moment().subtract(3, 'months').add(1, 'days').format('YYYY-MM-DD');
  const endTime = moment().format('YYYY-MM-DD');
  const overTime = moment().format('YYYY-MM-DD');
  const showTime = `${startTime} ~ ${endTime}`;
  const shortName = '三个月内';
  return { startTime, endTime, overTime, showTime, shortName };
}

// 一年以内
export function parseOneYear() {
  const startTime = moment().subtract(1, 'years').add(1, 'days').format('YYYY-MM-DD');
  const endTime = moment().format('YYYY-MM-DD');
  const overTime = moment().format('YYYY-MM-DD');
  const showTime = `${startTime} ~ ${endTime}`;
  const shortName = '一年内';
  return { startTime, endTime, overTime, showTime, shortName };
}

// 自定义时间段
export function parseInterval(start?: string, end?: string) {
  const startTime = start ? moment(start).format('YYYY-MM-DD') : '';
  const endTime = end ? moment(end).format('YYYY-MM-DD') : '';
  const overTime = end ? moment(end).format('YYYY-MM-DD') : '';
  const showTime = startTime && endTime ? `${startTime} ~ ${endTime}` : '';
  const shortName = '';
  return { startTime, endTime, overTime, showTime, shortName };
}

// 解析数字
export function formatNum(value: number | null) {
  let result: string;
  const num = parseFloat(`${value}`) || 0;
  if (num < 10000) {
    result = `${num}`;
  } else if (num < 100000000) {
    result = `${(num / 10000).toFixed(2)}万`;
  } else {
    result = `${(num / 100000000).toFixed(2)}亿`;
  }
  return result || 0;
}

// 解析时长
export function formatTime(seconds: number) {
  const hours = Math.ceil(seconds / 3600);
  return `${hours}小时`;
}
